import { useNavigate, Navigate } from 'react-router-dom';
import {
  Briefcase,
  AlertTriangle,
  FileText,
  ClipboardList,
  ChevronRight,
  CheckCircle,
  Plus,
} from 'lucide-react';
import { useDashboard, type ProjectSummaryDTO } from '../../hooks/useDashboard';
import { useAuthStore } from '../../stores/auth-store';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/Badge';
import { SkeletonTable, SkeletonText } from '../../components/ui/Skeleton';
import { AdvanceBar } from '../../components/shared/AdvanceBar';

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Buenos días';
  if (hour < 18) return 'Buenas tardes';
  return 'Buenas noches';
}

function formatDate(): string {
  return new Date().toLocaleDateString('es-EC', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);

  // Roles that don't have access to the executive dashboard
  const dashboardRoles = ['gerente', 'operaciones', 'administrativo'];
  const hasAccess = dashboardRoles.includes(user?.role ?? '');

  if (!hasAccess) {
    const redirectPath =
      user?.role === 'residente' ? '/proyectos' :
      user?.role === 'compras' ? '/despachos' :
      user?.role === 'contabilidad' ? '/contabilidad' :
      '/proyectos';
    return <Navigate to={redirectPath} replace />;
  }

  return <DashboardContent />;
}


function DashboardContent() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data, isLoading, isError, refetch } = useDashboard();

  if (isLoading) {
    return (
      <div className="space-y-6 fade-up">
        <SkeletonText lines={2} />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}><div className="p-4"><SkeletonText lines={2} /></div></Card>
          ))}
        </div>
        <SkeletonTable rows={3} cols={4} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-6 fade-up">
        <h1 className="text-2xl font-bold text-brand-dark">Dashboard</h1>
        <Card>
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">Error al cargar el dashboard</p>
            <Button variant="secondary" className="mt-4" onClick={() => refetch()}>
              Reintentar
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const { kpis, projects_summary } = data;
  const pendingApprovals = projects_summary.filter((p) => p.planillas_pendientes > 0);

  // Empty state
  if (kpis.proyectos_activos === 0) {
    return (
      <div className="space-y-6 fade-up">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark">
            {getGreeting()}, {user?.nombre?.split(' ')[0] ?? 'Usuario'}.
          </h1>
          <p className="text-sm text-gray-500">{formatDate()}</p>
        </div>
        <Card>
          <div className="py-16 text-center space-y-4">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto" />
            <h2 className="text-lg font-semibold text-brand-dark">No hay proyectos activos</h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Crea tu primer proyecto para empezar a gestionar proformas, planillas y más.
            </p>
            <Button
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={() => navigate('/proyectos/nuevo')}
            >
              Crear primer proyecto
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-up">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold text-brand-dark">
          {getGreeting()}, {user?.nombre?.split(' ')[0] ?? 'Usuario'}.
        </h1>
        <p className="text-sm text-gray-500">{formatDate()}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Proyectos Activos"
          value={kpis.proyectos_activos}
          icon={<Briefcase size={20} />}
          color="blue"
          onClick={() => navigate('/proyectos')}
          className="fade-up-1"
        />
        <KPICard
          title="Pedidos Pendientes"
          value={kpis.pedidos_pendientes}
          icon={<AlertTriangle size={20} />}
          color={kpis.pedidos_pendientes > 0 ? 'orange' : 'green'}
          onClick={() => navigate('/despachos')}
          className="fade-up-2"
        />
        <KPICard
          title="Planillas Pendientes"
          value={kpis.planillas_pendientes}
          icon={<ClipboardList size={20} />}
          color={kpis.planillas_pendientes > 0 ? 'orange' : 'green'}
          onClick={() => navigate('/planillas?estado=pendiente_administrativo')}
          className="fade-up-3"
        />
        <KPICard
          title="Proformas en Revisión"
          value={kpis.proformas_pendientes}
          icon={<FileText size={20} />}
          color={kpis.proformas_pendientes > 0 ? 'orange' : 'green'}
          onClick={() => navigate('/proformas?estado=enviada')}
          className="fade-up-4"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pedidos pendientes panel */}
        <Card>
          <div className="p-4">
            <h3 className="text-sm font-semibold text-brand-dark mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              Pedidos Pendientes de Despacho
            </h3>
            {kpis.pedidos_pendientes === 0 ? (
              <div className="flex items-center gap-2 text-sm text-green-600 py-4">
                <CheckCircle size={16} />
                No hay pedidos pendientes
              </div>
            ) : (
              <div className="space-y-1 py-2">
                <p className="text-2xl font-bold text-brand-dark">{kpis.pedidos_pendientes}</p>
                <p className="text-sm text-gray-500">pedido(s) esperando despacho</p>
                <button
                  className="text-xs text-[#3c98ff] hover:underline flex items-center gap-1 mt-2"
                  onClick={() => navigate('/despachos')}
                >
                  Ir a Despachos <ChevronRight size={12} />
                </button>
              </div>
            )}
          </div>
        </Card>

        {/* Pending approvals panel */}
        <Card>
          <div className="p-4">
            <h3 className="text-sm font-semibold text-brand-dark mb-3 flex items-center gap-2">
              <ClipboardList size={16} className="text-blue-500" />
              Pendientes de Aprobación
            </h3>
            {pendingApprovals.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-green-600 py-4">
                <CheckCircle size={16} />
                No hay aprobaciones pendientes
              </div>
            ) : (
              <div className="space-y-3">
                {pendingApprovals.slice(0, 5).map((p) => (
                  <PendingApprovalRow key={p.project_id} project={p} />
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* All active projects mini-table */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-brand-dark">
              Todos los Proyectos Activos
            </h3>
            <button
              onClick={() => navigate('/proyectos')}
              className="text-xs text-[#3c98ff] hover:underline flex items-center gap-1"
            >
              Ver todos los proyectos
              <ChevronRight size={12} />
            </button>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200 text-xs uppercase tracking-wider">
                  <th className="py-2 pr-4">Código</th>
                  <th className="py-2 pr-4">Nombre</th>
                  <th className="py-2 pr-4">Residente</th>
                  <th className="py-2 pr-4">Estado</th>
                  <th className="py-2 pr-4">% Ejecución</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {projects_summary.slice(0, 10).map((p) => (
                  <tr
                    key={p.project_id}
                    className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                    onClick={() => navigate(`/proyectos/${p.project_id}`)}
                  >
                    <td className="py-2 pr-4 font-mono text-xs text-gray-500">{p.codigo}</td>
                    <td className="py-2 pr-4 text-gray-700">{p.nombre}</td>
                    <td className="py-2 pr-4 text-gray-500 text-xs">{p.residente_nombre ?? '—'}</td>
                    <td className="py-2 pr-4">
                      <StatusBadge entity="project" status={p.estado} />
                    </td>
                    <td className="py-2 pr-4">
                      <AdvanceBar
                        consumed={p.total_ejecutado_proforma}
                        total={p.total_proforma}
                        variant="compact"
                        mode="progress"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <ChevronRight size={14} className="text-gray-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {projects_summary.slice(0, 10).map((p) => (
              <button
                key={p.project_id}
                className="w-full text-left rounded-lg border border-gray-100 p-3 hover:bg-gray-50"
                onClick={() => navigate(`/proyectos/${p.project_id}`)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800">{p.nombre}</span>
                  <StatusBadge entity="project" status={p.estado} />
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{p.codigo} · {p.client_nombre}</p>
                {p.total_proforma > 0 && (
                  <div className="mt-1.5">
                    <AdvanceBar consumed={p.total_ejecutado_proforma} total={p.total_proforma} variant="compact" mode="progress" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── KPI Card ──

function KPICard({
  title,
  value,
  icon,
  color,
  onClick,
  className = '',
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'orange' | 'red';
  onClick: () => void;
  className?: string;
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
  };

  const valueColors = {
    blue: 'text-brand-dark',
    green: 'text-green-700',
    orange: 'text-orange-700',
    red: 'text-red-700',
  };

  return (
    <Card className={`cursor-pointer hover:shadow-md transition-shadow ${className}`}>
      <button className="w-full p-4 text-left" onClick={onClick}>
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-gray-500">{title}</p>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color]}`}>
            {icon}
          </div>
        </div>
        <p className={`mt-2 text-3xl font-bold ${valueColors[color]}`}>{value}</p>
      </button>
    </Card>
  );
}


// ── Pending approval row ──

function PendingApprovalRow({ project }: { project: ProjectSummaryDTO }) {
  const navigate = useNavigate();

  return (
    <button
      className="w-full flex items-center justify-between rounded-lg border border-gray-100 p-3 text-left hover:bg-gray-50"
      onClick={() => navigate(`/planillas?project=${project.project_id}`)}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-800 truncate">{project.nombre}</p>
        <p className="text-xs text-gray-400">
          {project.planillas_pendientes} planilla(s) pendiente(s)
        </p>
      </div>
      <span className="text-xs text-[#3c98ff] hover:underline flex items-center gap-0.5 ml-3">
        Revisar
        <ChevronRight size={12} />
      </span>
    </button>
  );
}
